import { forwardRef, useEffect, useRef, type InputHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

export interface CheckboxProps
  extends Omit<InputHTMLAttributes<HTMLInputElement>, 'type'> {
  /** Render the third (mixed) visual state — for "select all" headers. */
  indeterminate?: boolean;
}

/**
 * Minimal styled checkbox. Wraps a native `<input type="checkbox">` so it stays
 * keyboard- + form-accessible; `indeterminate` is a DOM-only prop set via ref.
 */
export const Checkbox = forwardRef<HTMLInputElement, CheckboxProps>(
  ({ className, indeterminate = false, ...rest }, ref) => {
    const innerRef = useRef<HTMLInputElement | null>(null);

    useEffect(() => {
      if (innerRef.current) innerRef.current.indeterminate = indeterminate;
    }, [indeterminate]);

    return (
      <input
        ref={(node) => {
          innerRef.current = node;
          if (typeof ref === 'function') ref(node);
          else if (ref) ref.current = node;
        }}
        type="checkbox"
        className={cn(
          'h-4 w-4 cursor-pointer rounded border-border text-primary accent-primary',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1',
          className,
        )}
        {...rest}
      />
    );
  },
);
Checkbox.displayName = 'Checkbox';
